import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 1 RBAC: new role set + legacy aliasing so existing hasRole([...]) checks keep working.
const NEW_ROLES = [
  'super_admin', 'school_admin', 'head_of_operations', 'school_bursar',
  'project_manager', 'facility_manager', 'registrar', 'sponsorship_officer',
  'finance_officer', 'attendance_officer', 'hr_officer', 'volunteer_coordinator',
  'procurement_officer', 'teaching_staff', 'student',
];

// Legacy role name → set of new roles that should match it
const LEGACY_ROLE_MAP = {
  admin: ['super_admin', 'school_admin'],
  finance_officer: ['finance_officer'],
  school_head: ['school_admin', 'registrar'],
  loan_officer: ['finance_officer'],
  staff: ['attendance_officer', 'hr_officer', 'volunteer_coordinator', 'registrar'],
  board_member: ['school_admin'],
};

// ---------------------------------------------------------------------------
// Session storage helpers for the auth token.
//
// Decision: store JWT in sessionStorage (cleared on tab close) rather than
// localStorage (persists indefinitely).
// Trade-offs:
//   - sessionStorage still readable by JS — fully XSS-resistant storage would
//     require httpOnly cookies + a server session refactor (CSRF, SameSite, etc).
//     That's deferred to a dedicated security iteration.
//   - sessionStorage limits the bad-attacker window to the current tab session
//     and immediately invalidates the token on tab close, which is a meaningful
//     improvement at near-zero implementation cost.
// Backward-compat: on first load we migrate any pre-existing localStorage token
// over to sessionStorage so existing sessions don't get logged out.
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'suubi_token';
const readToken = () => {
  let t = sessionStorage.getItem(TOKEN_KEY);
  if (!t) {
    // Migrate from old localStorage location (one-time, then clear it)
    const legacy = localStorage.getItem(TOKEN_KEY);
    if (legacy) {
      sessionStorage.setItem(TOKEN_KEY, legacy);
      localStorage.removeItem(TOKEN_KEY);
      t = legacy;
    }
  }
  return t;
};
const writeToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
const clearToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY); // belt-and-braces
};

// Centralized token accessor — use this instead of touching storage directly.
export const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readToken());
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      logoutInternal();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;

      writeToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);

      return { success: true, mustChangePassword: !!userData.must_change_password };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Login failed'),
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Password change revokes all sessions — adopt the fresh token for
      // this device so the user stays logged in here.
      const newToken = res.data?.access_token;
      if (newToken) {
        writeToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
      }
      // Refetch user to clear must_change_password / weak_password flags
      await fetchUser();
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : 'Failed to change password',
      };
    }
  };

  const logoutInternal = () => {
    clearToken();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const logout = logoutInternal;

  /**
   * hasRole(allowed)
   * Accepts either new role names or legacy role names. super_admin always matches.
   * Legacy names are expanded to the new set via LEGACY_ROLE_MAP.
   */
  const hasRole = useCallback((allowed) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    const expanded = new Set(allowed);
    for (const k of Object.keys(LEGACY_ROLE_MAP)) {
      if (expanded.has(k)) {
        for (const r of LEGACY_ROLE_MAP[k]) expanded.add(r);
      }
    }
    return expanded.has(user.role);
  }, [user]);

  const hasModule = useCallback((moduleKey) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return (user.module_permissions || []).includes(moduleKey);
  }, [user]);

  /**
   * hasPermission(moduleKey, action='view')
   *
   * Phase A RBAC: checks the fine-grained permissions_v2 matrix.
   * Falls back to:
   *   - legacy module_permissions list (for view-only)
   *   - role-based check (for users that pre-date the matrix)
   * super_admin always passes.
   */
  const hasPermission = useCallback((moduleKey, action = 'view') => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const pv2 = user.permissions_v2;
    if (pv2 && typeof pv2 === 'object') {
      const block = pv2[moduleKey];
      if (block && block[action] === true) return true;
      // If matrix exists but the action is explicitly false, deny.
      if (block && typeof block[action] === 'boolean') return false;
    }
    // Legacy fallback: module_permissions grants 'view'
    if (action === 'view' && (user.module_permissions || []).includes(moduleKey)) return true;
    return false;
  }, [user]);

  /**
   * Iter27: hasDocumentScope(scope, action='view')
   * Returns whether the current user can view/upload documents in a given
   * scope (Schools / Women Development Program / General).
   * super_admin always passes.
   */
  const hasDocumentScope = useCallback((scope, action = 'view') => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const ds = user.document_scopes;
    if (ds && typeof ds === 'object') {
      const block = ds[scope];
      if (block && block[action] === true) return true;
      if (block && typeof block[action] === 'boolean') return false;
    }
    return false;
  }, [user]);

  const allowedDocumentScopes = useCallback((action = 'view') => {
    if (!user) return [];
    if (user.role === 'super_admin') return ['schools', 'women', 'general'];
    const ds = user.document_scopes || {};
    return ['schools', 'women', 'general'].filter((s) => ds[s] && ds[s][action]);
  }, [user]);

  const canEdit = () => hasRole(['admin', 'finance_officer', 'school_head', 'project_manager', 'loan_officer', 'staff']);
  const isReadOnly = () => hasRole(['board_member']);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      changePassword,
      hasRole,
      hasModule,
      hasPermission,
      hasDocumentScope,
      allowedDocumentScopes,
      canEdit,
      isReadOnly,
      NEW_ROLES,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
